import { useCallback, useEffect, useReducer, useRef } from "react";
import {
  conversationKind,
  conversationStep,
  initialConversation,
  recognitionRuns,
  type ConversationEvent,
  type ConversationKind,
  type ConversationState,
  type ConversationSupport,
} from "./miloConversationMode";

/**
 * The browser half of Milo's conversation mode (KEY-850): the Web Speech API's recognition for listening and
 * speechSynthesis for answering, driven by the pure machine in `miloConversationMode`.
 *
 * In Chrome and Edge the browser sends the audio to its own speech provider (Google or Microsoft) to be
 * recognised; Safari recognises on the device where it can. Only the recognised text goes to Milo.
 */

interface RecognitionAlternative {
  transcript: string;
}
interface RecognitionResult {
  readonly isFinal: boolean;
  readonly length: number;
  [index: number]: RecognitionAlternative;
}
interface RecognitionResultEvent {
  readonly results: { readonly length: number; [index: number]: RecognitionResult };
}
interface Recognition {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((e: RecognitionResultEvent) => void) | null;
  onend: (() => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  start(): void;
  abort(): void;
}
type RecognitionCtor = new () => Recognition;

function recognitionCtor(): RecognitionCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

function synthesis(): SpeechSynthesis | null {
  if (typeof window === "undefined" || !("speechSynthesis" in window) || typeof SpeechSynthesisUtterance === "undefined") return null;
  return window.speechSynthesis;
}

export function detectConversationSupport(): ConversationSupport {
  return { recognition: !!recognitionCtor(), synthesis: !!synthesis() };
}

/** How often the silence rules are checked. */
const TICK_MS = 200;
/** A recognition session that ends on its own (Safari, or Chrome after a long pause) restarts after this. */
const RESTART_MS = 250;

function reducer(state: ConversationState, event: ConversationEvent): ConversationState {
  return conversationStep(state, event);
}

export interface MiloConversation {
  state: ConversationState;
  /** "talk" (hands-free), "read" (read replies aloud only) or null (this browser can do neither: no toggle). */
  available: ConversationKind | null;
  on: boolean;
  start(): void;
  stop(notice?: string | null): void;
  /** Another route sent a turn (typed, or a proposal button): stop listening while Milo thinks. */
  sending(): void;
  replied(text: string, turnsLeft: number | null, proposed: boolean): void;
  failed(): void;
  typing(): void;
}

/** `onTurn` sends what was said as a Milo turn; the dock reports the outcome back with `replied` or `failed`. */
export function useMiloConversation(onTurn: (text: string) => void): MiloConversation {
  const [state, dispatch] = useReducer(reducer, undefined, () => initialConversation(detectConversationSupport()));
  const stateRef = useRef(state);
  stateRef.current = state;
  const onTurnRef = useRef(onTurn);
  onTurnRef.current = onTurn;

  const recognitionRef = useRef<Recognition | null>(null);
  const restartRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  const on = state.kind !== null;
  const listen = recognitionRuns(state);

  // The silence clock: only while the mode is on.
  useEffect(() => {
    if (!on) return;
    const id = setInterval(() => dispatch({ type: "tick", now: Date.now() }), TICK_MS);
    return () => clearInterval(id);
  }, [on]);

  // A turn the machine has decided to send.
  useEffect(() => {
    if (state.outgoing === null) return;
    const text = state.outgoing;
    dispatch({ type: "taken" });
    onTurnRef.current(text);
  }, [state.outgoing]);

  // The microphone runs exactly while the machine is listening; anything else aborts it at once.
  useEffect(() => {
    if (!listen) return;
    const Ctor = recognitionCtor();
    if (!Ctor) return;
    let alive = true;

    const begin = () => {
      if (!alive) return;
      const r = new Ctor();
      r.continuous = true;
      r.interimResults = true;
      r.lang = navigator.language || "en-GB";
      r.onresult = (e) => {
        if (recognitionRef.current !== r) return;
        let final = "";
        let interim = "";
        for (let i = 0; i < e.results.length; i++) {
          const res = e.results[i];
          const t = res[0]?.transcript ?? "";
          if (res.isFinal) final += t;
          else interim += t;
        }
        dispatch({ type: "heard", final: final.trim(), interim: interim.trim(), now: Date.now() });
      };
      r.onerror = (e) => {
        if (recognitionRef.current !== r) return;
        dispatch({ type: "recognitionError", error: e.error });
      };
      r.onend = () => {
        if (recognitionRef.current !== r) return;
        recognitionRef.current = null;
        dispatch({ type: "recognitionEnded" });
        // Safari's continuous mode ends by itself; while we are still listening, listen again.
        if (alive && recognitionRuns(stateRef.current)) restartRef.current = setTimeout(begin, RESTART_MS);
      };
      recognitionRef.current = r;
      try {
        r.start();
      } catch {
        recognitionRef.current = null;
        dispatch({ type: "recognitionError", error: "start-failed" });
      }
    };

    begin();
    return () => {
      alive = false;
      if (restartRef.current) clearTimeout(restartRef.current);
      restartRef.current = null;
      const r = recognitionRef.current;
      recognitionRef.current = null;
      if (r) {
        try {
          r.abort();
        } catch {
          // Already stopped.
        }
      }
    };
  }, [listen]);

  // The voice: say the current line while speaking; be quiet the moment the phase changes.
  useEffect(() => {
    const synth = synthesis();
    if (!synth || state.phase !== "speaking" || !state.say) return;
    const u = new SpeechSynthesisUtterance(state.say);
    u.lang = navigator.language || "en-GB";
    const done = () => {
      if (utteranceRef.current !== u) return;
      utteranceRef.current = null;
      dispatch({ type: "spoken", now: Date.now() });
    };
    u.onend = done;
    u.onerror = done;
    utteranceRef.current = u;
    synth.cancel();
    synth.speak(u);
    return () => {
      if (utteranceRef.current === u) {
        utteranceRef.current = null;
        synth.cancel();
      }
    };
  }, [state.phase, state.say, state.sayId]);

  // A hidden tab ends it: nobody should be overheard by a tab they can't see.
  useEffect(() => {
    if (!on) return;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") dispatch({ type: "stop" });
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [on]);

  const start = useCallback(() => {
    // Called from the toggle's click: speaking something (silently) inside the gesture unlocks speech for later.
    const synth = synthesis();
    if (synth) {
      try {
        const primer = new SpeechSynthesisUtterance(" ");
        primer.volume = 0;
        synth.cancel();
        synth.speak(primer);
      } catch {
        // Some engines refuse an empty line; the real reply will try again.
      }
    }
    dispatch({ type: "start", now: Date.now() });
  }, []);

  const stop = useCallback((notice?: string | null) => {
    synthesis()?.cancel();
    dispatch({ type: "stop", notice });
  }, []);

  return {
    state,
    available: conversationKind(state.support),
    on,
    start,
    stop,
    sending: useCallback(() => dispatch({ type: "sending" }), []),
    replied: useCallback((text: string, turnsLeft: number | null, proposed: boolean) => dispatch({ type: "reply", text, turnsLeft, proposed, now: Date.now() }), []),
    failed: useCallback(() => dispatch({ type: "failed" }), []),
    typing: useCallback(() => dispatch({ type: "typing", now: Date.now() }), []),
  };
}
